class ImageInputHandler {
  constructor(settings) {
    this.settings = settings;

    // Default minimum dimensions
    this.minWidth = settings.minWidth || 48;
    this.minHeight = settings.minHeight || 48;
    this.acceptedTypes = ALLOWED_MIME;

    this.dragCounter = 0;

    this.init();
  }

  // Initialize listeners and input attributes
  init() {
    this.input = document.querySelector(this.settings.selectors.input);
    this.label = document.querySelector(this.settings.selectors.label);
    this.blobImage = null;

    if (!this.input || !this.label) return;

    this.input.setAttribute('accept', Object.keys(this.acceptedTypes).join(','));
    this.input.addEventListener('change', e => this.handleFiles(e.target.files));

    // Drag and drop support
    document.body.addEventListener('dragenter', e => {
      if (e.dataTransfer?.items?.[0]?.kind === 'file') {
        this.dragCounter++;
        this.label.classList.add(this.settings.classNames.drop);
      }
    });

    document.body.addEventListener('dragleave', () => {
      this.dragCounter = Math.max(this.dragCounter - 1, 0);
      if (this.dragCounter === 0) {
        this.label.classList.remove(this.settings.classNames.drop);
      }
    });

    document.body.addEventListener('dragover', e => e.preventDefault());

    document.body.addEventListener('dragend', () => {
      this.dragCounter = 0;
      this.label.classList.remove(this.settings.classNames.drop);
    });

    document.body.addEventListener('drop', e => {
      e.preventDefault();
      this.dragCounter = 0;
      this.label.classList.remove(this.settings.classNames.drop);
      if (e.dataTransfer.files.length > 0) {
        this.handleFiles(e.dataTransfer.files);
      }
    });
  }

  // Checks if MIME type is allowed
  isAcceptedType(type) {
    return type in this.acceptedTypes;
  }

  // Validates image dimensions
  validateImage(img, file) {
    const isVector = this.acceptedTypes[file.type] == "svg";
    if ((img.width < this.minWidth || img.height < this.minHeight) && !isVector) {
      this.onInvalidImage(`The image ${file.name} is smaller than ${this.minWidth}×${this.minHeight}.`);
      return false;
    }
    return true;
  }

  // Handles dropped or selected files
  handleFiles(files, cb) {
    const file = files[0];
    if (!file || !this.isAcceptedType(file.type)) return;

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        if (!this.validateImage(img, file)) return;
        this.previewImage = img;
        this.blobImage = file;
        this.onValidImage(file, img);
        cb?.();
      };
      img.onerror = () => this.onInvalidImage();
      img.src = reader.result;
    };
    reader.readAsDataURL(file);

    this.input.value = ''; // Clear file input
  }

  // Optional public utility
  getExtensionFromType(type) {
    return this.acceptedTypes[type] || 'bin';
  }

  // Loads from inline SVG text
  loadFromSVG(svgText) {
    const type = 'image/svg+xml';
    if (!this.isAcceptedType(type)) {
      this.onInvalidImage("SVG not accepted.");
      return;
    }

    const blob = new Blob([svgText], { type });
    const file = new File([blob], this.settings.demoFileName, { type });

    this.handleFiles([file]);
  }

  // Success callback
  onValidImage(file, img) {
    if (typeof this.settings.onImageLoaded === 'function') {
      this.settings.onImageLoaded(file, img);
    }
  }

  // Error callback
  onInvalidImage(message) {
    if (typeof this.settings.onInvalidImage === 'function') {
      this.settings.onInvalidImage(message);
    } else {
      console.warn(message || "Invalid image.");
    }
  }
}

export default ImageInputHandler;
